/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { type FunctionComponent } from 'react'

import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiPanel, EuiSpacer, EuiText } from '@elastic/eui'

import { DocLink } from '@modules/doclinks'
import { FormattedMessage } from '@modules/i18n'

import useBillingNotificationsNav from '../hooks/useBillingNotificationsNav'

import BudgetTourSecondStep from './BudgetTour/BudgetTourSecondStep'

const EmptyState: FunctionComponent = () => {
  const { goToCreateBudget } = useBillingNotificationsNav()

  const handleAddBudget = () => goToCreateBudget()

  return (
    <EuiFlexGroup>
      <EuiFlexItem>
        <EuiText size='s' color='subdued'>
          <FormattedMessage
            id='billing.notifications.budgets.empty-state.description'
            defaultMessage="Add a budget and get email alerts when your organization's total usage exceeds a specific threshold. This helps you track spending without any interruption to your services. {learnMore}"
            values={{
              learnMore: (
                <DocLink
                  data-test-subj='billing.notifications.budgets.empty-state.description.learn-more'
                  link='billingCreateBudgetEmails'
                  showExternalLinkIcon={true}
                >
                  <FormattedMessage
                    id='billing.notifications.budgets.empty-state.description.learn-more'
                    defaultMessage='Learn more'
                  />
                </DocLink>
              ),
            }}
          />
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiPanel
          color='subdued'
          hasBorder={false}
          hasShadow={false}
          paddingSize='l'
          css={css({ textAlign: 'center' })}
        >
          <EuiText size='m'>
            <h2>
              <FormattedMessage
                id='billing.notifications.budgets.empty-state.add-budget.title'
                defaultMessage='Add a budget'
              />
            </h2>
          </EuiText>
          <EuiSpacer size='m' />
          <EuiText size='s' color='subdued'>
            <FormattedMessage
              id='billing.notifications.budgets.empty-state.add-budget.description'
              defaultMessage='Track your actual spending against a planned amount. Costs are updated within 24 hours.'
            />
          </EuiText>
          <EuiSpacer size='l' />
          <BudgetTourSecondStep>
            <EuiButton fill={true} onClick={handleAddBudget}>
              <FormattedMessage
                id='billing.notifications.budgets.empty-state.add-budget.button'
                defaultMessage='Add budget'
              />
            </EuiButton>
          </BudgetTourSecondStep>
        </EuiPanel>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default EmptyState
